import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useDropzone } from 'react-dropzone';

import CloudIcon from '../../../../../assets/icons/cloud-upload-outlined.png';
import PdfIcon from '../../../../../assets/icons/documents/pdf_OneDrive_icon 1.png';
import CustomButton from '../../../../../common/CustomButton';
import CustomInput from '../../../../../common/CustomInput';
import CustomMultiselect from '../../../../../common/CustomMultiselect';
import Tags from '../../../../../components/Tags';
import TripDocumentTagsField from './TripDocumentTagsField';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import {
  useAddDocumentMutation,
  useDeleteDocumetMutation,
  useGetTripDocumentsQuery,
  useUpdateDocumentMutation,
} from '../../../../../store/services/documentsApi';
import { useGetTagsQuery } from '../../../../../store/services/tagsApi';
import { setTags, Tag } from '../../../../../store/slices/tagsSlice';
import { TripDocument, TripDocumentAttachment } from '../../../../../types/trip.types';

import styles from './TripDocuments.module.scss';
import { DeleteIcon } from '../../../../../components/Icons';

type TripDocumentsProps = {
  tripId: number;
};

type LocalNewAttachment = {
  tempId: string;
  file: File;
};

const getAttachmentName = (attachmentPath: string) => {
  if (!attachmentPath) return 'Unknown file';
  const withoutQuery = attachmentPath.split('?')[0].split('#')[0];
  const name = withoutQuery.split('/').pop();
  try {
    return decodeURIComponent(name || 'Unknown file');
  } catch {
    return name || 'Unknown file';
  }
};

const TripDocuments: FC<TripDocumentsProps> = ({ tripId }) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const { tags } = useAppSelector(state => state.tags);

  const [addDocument] = useAddDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const [deleteDocument] = useDeleteDocumetMutation();

  const { data: tagsResponse } = useGetTagsQuery({});
  const {
    data: tripDocumentsResponse,
    isLoading: isDocumentsLoading,
    isFetching: isDocumentsFetching,
    refetch: refetchDocuments,
  } = useGetTripDocumentsQuery(tripId, { skip: !tripId });

  const [openManageTags, setOpenManageTags] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<number | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [documentTags, setDocumentTags] = useState<Tag[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<TripDocumentAttachment[]>([]);
  const [newAttachments, setNewAttachments] = useState<LocalNewAttachment[]>([]);
  const [attachmentsForDeletion, setAttachmentsForDeletion] = useState<number[]>([]);
  const [selectedFilterTags, setSelectedFilterTags] = useState<Tag[]>([]);
  const [fileSearch, setFileSearch] = useState('');
  const [requestError, setRequestError] = useState<string | null>(null);
  const [documentPendingDelete, setDocumentPendingDelete] = useState<{ id: number; name: string } | null>(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);

  const tripDocuments = (tripDocumentsResponse || []) as TripDocument[];

  useEffect(() => {
    if (tagsResponse) {
      dispatch(setTags(tagsResponse));
    }
  }, [dispatch, tagsResponse]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setIsEditing(false);
    setEditingDocumentId(null);
    setDocumentName('');
    setDocumentTags([]);
    setExistingAttachments([]);
    setNewAttachments([]);
    setAttachmentsForDeletion([]);
    setRequestError(null);
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const mappedFiles = acceptedFiles.map(file => ({
      tempId: `${Date.now()}-${file.name}-${Math.random()}`,
      file,
    }));
    setNewAttachments(prev => [...prev, ...mappedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleOpenModalCreate = () => {
    setIsEditing(false);
    setEditingDocumentId(null);
    setDocumentName('');
    setDocumentTags([]);
    setExistingAttachments([]);
    setNewAttachments([]);
    setAttachmentsForDeletion([]);
    setRequestError(null);
    setOpenModal(true);
  };

  const handleEditDocument = (document: TripDocument) => {
    setIsEditing(true);
    setEditingDocumentId(document.id);
    setDocumentName(document.name);
    setDocumentTags(document.tags || []);
    setExistingAttachments(document.attachments || []);
    setAttachmentsForDeletion([]);
    setNewAttachments([]);
    setOpenModal(true);
    setRequestError(null);
  };

  const handleRemoveExistingAttachment = (attachmentId: number) => {
    setAttachmentsForDeletion(prev =>
      prev.includes(attachmentId) ? prev.filter(id => id !== attachmentId) : [...prev, attachmentId]
    );
  };

  const handleRemoveNewAttachment = (tempId: string) => {
    setNewAttachments(prev => prev.filter(file => file.tempId !== tempId));
  };

  const handleDownloadAttachment = async (attachmentPath: string) => {
    try {
      const resolvedUrl = /^https?:\/\//i.test(attachmentPath)
        ? attachmentPath
        : new URL(attachmentPath, process.env.REACT_APP_API_URL).toString();

      const link = document.createElement('a');
      link.href = resolvedUrl;
      link.download = getAttachmentName(attachmentPath);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      setRequestError('Unable to download file. Please try again.');
    }
  };

  const handleSaveDocument = async () => {
    try {
      const formData = new FormData();
      formData.append('name', documentName.trim());
      documentTags.forEach(tag => {
        formData.append('tags', String(tag.id));
      });

      if (isEditing && editingDocumentId !== null) {
        newAttachments.forEach(attachment => {
          formData.append('attachments_for_addition', attachment.file);
        });
        attachmentsForDeletion.forEach(attachmentId => {
          formData.append('attachments_for_deletion', String(attachmentId));
        });

        await updateDocument({ id: editingDocumentId, formData }).unwrap();
      } else {
        formData.append('trip', String(tripId));
        newAttachments.forEach(attachment => {
          formData.append('attachments', attachment.file);
        });

        await addDocument(formData).unwrap();
      }

      handleCloseModal();
      refetchDocuments();
    } catch (error) {
      console.error('Failed to save document:', error);
      setRequestError('Unable to save document. Please try again.');
    }
  };

  const handleRequestDeleteDocument = (id: number, name: string) => {
    setDocumentPendingDelete({ id, name });
  };

  const handleCancelDeleteDocument = () => {
    if (!isDeletingDocument) setDocumentPendingDelete(null);
  };

  const handleConfirmDeleteDocument = async () => {
    if (!documentPendingDelete) return;

    setIsDeletingDocument(true);
    try {
      await deleteDocument({ id: documentPendingDelete.id }).unwrap();
      if (openModal && editingDocumentId === documentPendingDelete.id) {
        handleCloseModal();
      }
      setDocumentPendingDelete(null);
      refetchDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
      if (openModal && editingDocumentId === documentPendingDelete.id) {
        setRequestError('Unable to delete document. Please try again.');
      }
    } finally {
      setIsDeletingDocument(false);
    }
  };

  const filteredDocuments = useMemo(() => {
    const normalizedSearch = fileSearch.trim().toLowerCase();
    const selectedTagIds = selectedFilterTags.map(tag => tag.id);

    return tripDocuments.filter(document => {
      const hasAllTags =
        selectedTagIds.length === 0 || selectedTagIds.every(tagId => document.tags.some(tag => tag.id === tagId));

      if (!hasAllTags) return false;
      if (!normalizedSearch) return true;

      return document.name.toLowerCase().includes(normalizedSearch);
    });
  }, [fileSearch, selectedFilterTags, tripDocuments]);

  const remainingExistingCount = existingAttachments.filter(a => !attachmentsForDeletion.includes(a.id)).length;
  const totalFilesInModal = remainingExistingCount + newAttachments.length;

  const isSaveDisabled =
    documentName.trim().length === 0 ||
    documentTags.length > 5 ||
    (!isEditing && newAttachments.length === 0) ||
    (isEditing && totalFilesInModal === 0);

  return (
    <Box className={styles.tripDocuments}>
      <Box className={styles.filterSection}>
        <Box className={styles.filtersRow}>
          <Box className={styles.tagsFilter}>
            <CustomMultiselect
              tags={tags}
              tagsOptions={selectedFilterTags}
              setTagsOptions={setSelectedFilterTags}
            />
          </Box>
          <Box className={styles.actionsTop}>
            <CustomButton
              variant="contained"
              onClick={handleOpenModalCreate}
              customClass={styles.addDocButton}
            >
              Add document
            </CustomButton>
            <CustomButton
              variant="outlined"
              onClick={() => setOpenManageTags(true)}
              customClass={styles.manageTagsButton}
            >
              Manage tags
            </CustomButton>
          </Box>
        </Box>
        <TextField
          value={fileSearch}
          onChange={e => setFileSearch(e.target.value)}
          placeholder="Search by document name"
          size="small"
          fullWidth
          className={styles.searchInput}
        />
      </Box>

      <Box className={styles.content}>
        {(isDocumentsLoading || isDocumentsFetching) && <Typography variant="h11">Loading documents...</Typography>}

        {!isDocumentsLoading && filteredDocuments.length === 0 && (
          <Typography
            variant="h11"
            className={styles.emptyState}
          >
            No documents found for this trip.
          </Typography>
        )}

        {filteredDocuments.map(document => (
          <Box
            key={document.id}
            className={styles.documentCard}
          >
            <Box className={styles.documentMain}>
              <Box className={styles.summary}>
                <Typography
                  variant="h9Bold"
                  className={styles.documentName}
                >
                  {document.name}
                </Typography>
                {document.attachments.length > 0 ? (
                  <Box className={styles.attachmentsBlock}>
                    {document.attachments.map(attachment => (
                      <Box
                        key={attachment.id}
                        className={styles.fileRow}
                      >
                        <Box className={styles.fileRowIcon}>
                          <img
                            src={PdfIcon}
                            alt="pdf"
                          />
                        </Box>
                        <Typography
                          variant="h11"
                          className={styles.fileRowName}
                        >
                          {getAttachmentName(attachment.content)}
                        </Typography>
                        <IconButton
                          size="small"
                          className={styles.downloadIconButton}
                          onClick={() => handleDownloadAttachment(attachment.content)}
                          aria-label="Download file"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="h13"
                    className={styles.noFiles}
                  >
                    No files attached
                  </Typography>
                )}
                <Box className={styles.tags}>
                  {document.tags.map(tag => (
                    <Typography
                      key={tag.id}
                      variant="h12Semibold"
                      className={styles.tag}
                      style={{ backgroundColor: tag.color ?? '#E0E0E0', color: '#222B3A' }}
                    >
                      {tag.name}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
            <Box className={styles.actions}>
              <IconButton
                className={styles.deleteIconButton}
                onClick={() => handleRequestDeleteDocument(document.id, document.name)}
              >
                <DeleteIcon />
              </IconButton>
              <IconButton
                className={styles.editIconButton}
                onClick={() => handleEditDocument(document)}
              >
                <EditOutlinedIcon />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth={false}
        sx={
          isMobile
            ? {
                '& .MuiDialog-paper': {
                  width: '100vw',
                  maxWidth: '100vw',
                  margin: isMobile ? '16px 0' : '32px 0',
                  borderRadius: '20px',
                },
              }
            : {
                maxWidth: '546px',
                margin: 'auto',
                '& .MuiDialog-paper': {
                  borderRadius: '20px',
                },
              }
        }
      >
        <DialogTitle
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            color: '#222B3A',
          }}
        >
          <Typography variant="h5Bold">{isEditing ? documentName : 'Add Document'}</Typography>
          <CloseIcon
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              '&:hover': {
                cursor: 'pointer',
              },
            }}
          />
        </DialogTitle>
        <DialogContent>
          <CustomInput
            inputLabel="Name"
            value={documentName}
            onChange={e => setDocumentName(e.target.value)}
          />
          <TripDocumentTagsField
            value={documentTags}
            onChange={setDocumentTags}
            maxTags={5}
          />
          <br />
          <Typography
            variant="h11Semibold"
            color="#222B3A"
          >
            Attach Documents
          </Typography>
          <Box
            {...getRootProps()}
            style={{
              padding: '24px',
              marginBottom: '16px',
              border: '2px dashed',
              borderRadius: '20px',
              borderColor: isDragActive ? '#1976d2' : '#bdbdbd',
              backgroundColor: isDragActive ? 'rgba(25, 118, 210, 0.08)' : '#fff',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <input {...getInputProps()} />
            <img
              src={CloudIcon}
              alt="cloud"
              className={styles.cloudIcon}
            />
            <Typography
              variant="h12Semibold"
              gutterBottom
              color="#222B3A"
            >
              Select your file
            </Typography>
            <Typography
              variant="h13"
              color="#5E6A81"
            >
              png, pdf, jpg, docx accepted
            </Typography>
          </Box>

          {totalFilesInModal > 0 && (
            <Box className={styles.attachmentsList}>
              <List
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {existingAttachments.map(attachment => {
                  const markedForDeletion = attachmentsForDeletion.includes(attachment.id);
                  if (markedForDeletion) return null;

                  return (
                    <ListItem
                      key={attachment.id}
                      className={styles.attachmentListItem}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveExistingAttachment(attachment.id)}
                          className={styles.deleteIconButton}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        <img
                          src={PdfIcon}
                          alt="pdf icon"
                        />
                      </ListItemIcon>
                      <ListItemText
                        className={styles.attachmentFileName}
                        primary={getAttachmentName(attachment.content)}
                        primaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItem>
                  );
                })}
                {newAttachments.map(attachment => (
                  <ListItem
                    key={attachment.tempId}
                    className={styles.attachmentListItem}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveNewAttachment(attachment.tempId)}
                        className={styles.deleteIconButton}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      <img
                        src={PdfIcon}
                        alt="pdf icon"
                      />
                    </ListItemIcon>
                    <ListItemText
                      className={styles.attachmentFileName}
                      primary={attachment.file.name}
                      secondary={`${(attachment.file.size / 1024).toFixed(2)} KB`}
                      primaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {requestError && (
            <Typography
              variant="h12"
              sx={{ color: '#C8102E', mt: 1 }}
            >
              {requestError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px 20px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyItems: 'center',
              alignContent: 'center',
              gap: '10px',
              '& button': {
                height: '44px',
              },
            }}
          >
            {isEditing ? (
              <>
                <CustomButton
                  variant="outlined"
                  onClick={() =>
                    editingDocumentId !== null &&
                    handleRequestDeleteDocument(editingDocumentId, documentName.trim() || 'this document')
                  }
                  customClass={styles.deleteDocumentButton}
                >
                  Delete Document
                </CustomButton>
                <CustomButton
                  variant="contained"
                  onClick={handleSaveDocument}
                  disabled={isSaveDisabled}
                >
                  Save Changes
                </CustomButton>
              </>
            ) : (
              <>
                <CustomButton
                  variant="outlined"
                  onClick={handleCloseModal}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="contained"
                  onClick={handleSaveDocument}
                  disabled={isSaveDisabled}
                >
                  Add Document
                </CustomButton>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Modal
        open={Boolean(documentPendingDelete)}
        onClose={() => {
          if (!isDeletingDocument) handleCancelDeleteDocument();
        }}
        disableEscapeKeyDown={isDeletingDocument}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: 'fit-content',
            minWidth: 355,
            width: '100%',
            bgcolor: 'background.paper',
            borderRadius: '20px',
            p: '32px 32px',
            overflowY: 'auto',
            boxShadow: 'none',
            maxHeight: '90vh',
            '@media (max-width: 900px)': {
              p: '32px 15px',
            },
          }}
        >
          <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: isDeletingDocument ? 'default' : 'pointer' }}>
            <CloseIcon
              sx={{ width: '30px', height: '30px', opacity: isDeletingDocument ? 0.5 : 1 }}
              onClick={handleCancelDeleteDocument}
            />
          </Box>
          <Box className={styles.deleteConfirmModalContent}>
            <Typography
              variant="h5Bold"
              className={styles.deleteConfirmTitle}
              color="#222B3A"
            >
              Are you sure?
            </Typography>
            <Typography
              variant="h10"
              className={styles.deleteConfirmTitle}
            >
              {`You want to remove the document “${documentPendingDelete?.name ?? ''}”?`}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '30px',
                width: '100%',
                gap: '16px',
                '& button': {
                  height: '44px',
                },
              }}
            >
              <CustomButton
                variant="outlined"
                onClick={handleCancelDeleteDocument}
                disabled={isDeletingDocument}
                customClass={styles.deleteConfirmCancelBtn}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={handleConfirmDeleteDocument}
                disabled={isDeletingDocument}
                customClass={styles.deleteConfirmDeleteBtn}
              >
                Delete document
              </CustomButton>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Tags
        openManageFilters={openManageTags}
        tags={tags}
        toggleManageDrawer={setOpenManageTags}
      />
    </Box>
  );
};

export default TripDocuments;
